import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { ContactDetail, ContactListItem, PaginatedResponse } from '@repo/types'
import { api } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { nonNegativeParam, withDayTimeZone } from '@/lib/utils'
import { dashboardKeys } from '@/hooks/api/dashboard'
import { dealKeys } from '@/hooks/api/deal-keys'
import { reportKeys } from '@/hooks/api/reports'
import { tiperKeys } from '@/hooks/api/tipers'

/**
 * Dátová vrstva `/contacts` — kontakt je jediná osoba CRM (kontakt aj zákazník). Všetky kľúče sú pod
 * prefixom ['contacts'], takže `contactKeys.all` obnoví zoznam, detail, výber osoby aj výber agentov.
 * Reakcie stránok (navigácia, zatvorenie modalu, inline chyba) ostávajú u volajúceho cez `mutate(vars, { onSuccess })`.
 */

export interface ContactListFilters {
  page: number
  status?: string
  contactType?: string
  interest?: string
  propertyType?: string
  propertySubtype?: string
  region?: string
  district?: string
  priceFrom?: string
  priceTo?: string
  source?: string
  agentId?: string
  /** Len osoby bez agenta. */
  pool?: boolean
  dateFrom?: string
  dateTo?: string
  /** Pokročilé podmienky (`adv` z URL, už prešli kontrolou). */
  adv?: string
  search?: string
}

export const contactKeys = {
  all: ['contacts'] as const,
  lists: ['contacts', 'list'] as const,
  list: (filters: ContactListFilters) => ['contacts', 'list', filters] as const,
  details: ['contacts', 'detail'] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
  agents: ['contacts', 'agents'] as const,
  assignableAgents: ['contacts', 'assignable-agents'] as const,
  picker: (clientTypeOnly: boolean) => ['contacts', 'picker', clientTypeOnly] as const,
  phoneLookup: (phones: string[]) => ['contacts', 'phone-lookup', phones] as const,
}

/** Používateľ vo filtri „Agent" (aktuálni aj historickí agenti osôb). */
export interface ContactPersonOption {
  id: string
  firstName: string
  lastName: string
}

/** Maklér, ktorého môže prihlásený používateľ vybrať za agenta (pobočku vynucuje server). */
export interface AssignableAgent extends ContactPersonOption {
  avatarUrl: string | null
  role: string
}

// Textové filtre zoznamu, ktoré idú do query bez úpravy (ceny overí `nonNegativeParam`, pool je boolean).
const TEXT_FILTERS = [
  'status', 'contactType', 'interest', 'propertyType', 'propertySubtype', 'region', 'district',
  'source', 'agentId', 'dateFrom', 'dateTo', 'search', 'adv',
] as const

export function useContacts(filters: ContactListFilters) {
  return useQuery({
    queryKey: contactKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(filters.page), limit: '25' })
      for (const key of TEXT_FILTERS) {
        const value = filters[key]
        if (value) params.set(key, value)
      }
      const priceFrom = nonNegativeParam(filters.priceFrom)
      if (priceFrom) params.set('priceFrom', priceFrom)
      const priceTo = nonNegativeParam(filters.priceTo)
      if (priceTo) params.set('priceTo', priceTo)
      if (filters.pool) params.set('pool', 'true')
      // Chybu ukazuje zoznam na mieste tabuľky (ContactsPage), toast by ju zdvojil.
      const { data } = await api.get<PaginatedResponse<ContactListItem>>(`/contacts?${withDayTimeZone(params)}`, { skipErrorToast: true })
      return data
    },
    retry: (failureCount, err: unknown) => {
      // 400 = server filter odmietol (napr. pokročilým podmienkam vyhovuje priveľa kontaktov) — opakovanie dopadne
      // rovnako. Ostatné chyby raz, ako predvolený QueryClient.
      if ((err as { response?: { status?: number } })?.response?.status === 400) return false
      return failureCount < 1
    },
  })
}

export async function fetchContact(id: string, config?: { skipErrorToast?: boolean }): Promise<ContactDetail> {
  const { data } = await api.get<ContactDetail>(`/contacts/${id}`, config)
  return data
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: contactKeys.detail(id ?? ''),
    queryFn: () => fetchContact(id!),
    enabled: !!id,
    retry: (failureCount, err: unknown) => {
      // 404 = osoba je v koši alebo mimo tenanta — opakovanie nepomôže.
      if ((err as { response?: { status?: number } })?.response?.status === 404) return false
      return failureCount < 2
    },
  })
}

/** Možnosti filtra „Agent" v zozname. TIPER ho nemá (API mu cestu nepovolí). */
export function useContactAgentOptions(enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.agents,
    queryFn: async () => (await api.get<ContactPersonOption[]>('/contacts/agents')).data,
    enabled,
  })
}

export function useAssignableAgents(enabled = true) {
  return useQuery({
    queryKey: contactKeys.assignableAgents,
    queryFn: async () => (await api.get<AssignableAgent[]>('/contacts/assignable-agents')).data,
    enabled,
  })
}

/**
 * Osoba sa zobrazuje v zozname, detaile, výbere osoby, v Reportoch (lievik), na dashboarde a ako strana
 * obchodu („Doplňte údaje") — po každej zmene obnoviť všetky. Detail zmazanej osoby (`removedId`) sa už
 * nenačítava: jeho 404 by stránku presmerovala skôr, než sama odíde na zoznam.
 */
export function invalidateContactViews(qc: QueryClient, removedId?: string) {
  qc.invalidateQueries({
    queryKey: contactKeys.all,
    predicate: removedId ? (q) => !(q.queryKey[1] === 'detail' && q.queryKey[2] === removedId) : undefined,
  })
  qc.invalidateQueries({ queryKey: reportKeys.all })
  qc.invalidateQueries({ queryKey: dashboardKeys.stats })
  qc.invalidateQueries({ queryKey: dealKeys.details })
}

/** Úprava polí osoby. `skipErrorToast` = chybu ukáže volajúci sám (formulár detailu). */
export function useUpdateContact(id: string | undefined, { skipErrorToast = false } = {}) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch<ContactDetail>(`/contacts/${id}`, payload, { skipErrorToast }).then((r) => r.data),
    onSuccess: () => invalidateContactViews(qc),
  })
}

/** „Pridať agenta" — agentov osoby len pribúda. Chybu ukazuje modal. */
export function useAddContactAgent(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<ContactDetail>(`/contacts/${id}/agents`, { userId }, { skipErrorToast: true }).then((r) => r.data),
    onSuccess: () => invalidateContactViews(qc),
  })
}

/**
 * Prevziať osobu z poolu. Agent preberá za seba, asistentka a super admin posielajú makléra (`brokerId`).
 * 409 s menom = osobu medzitým prevzal iný maklér (toast interceptora).
 */
export function useTakeContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, brokerId }: { id: string; brokerId?: string | null }) =>
      api.post<ContactDetail>(`/contacts/${id}/take`, brokerId ? { brokerId } : {}).then((r) => r.data),
    onSuccess: () => invalidateContactViews(qc),
  })
}

/** „Pracovať s ním" pri existujúcej osobe — konajúci (alebo vybraný maklér) sa stane agentom. Chybu ukazuje volajúci. */
export async function claimContact(id: string, brokerId?: string | null): Promise<ContactDetail> {
  const res = await api.post<ContactDetail>(`/contacts/${id}/claim`, brokerId ? { brokerId } : {}, { skipErrorToast: true })
  return res.data
}

// Jediné miesto s cestou prihlásenia na newsletter (modul newsletter v API).
const newsletterPath = (id: string) => `/newsletter/contacts/${id}`

export interface CreateContactInput {
  payload: Record<string, unknown>
  /** Prvý záujem — len typ Záujemca. */
  interest?: Record<string, unknown>
  /** Prihlásiť na newsletter (vyžaduje e-mail). */
  newsletter?: boolean
}

/**
 * Založí osobu zo sprievodcu: POST /contacts, potom voliteľne prvý záujem a newsletter. Keď zlyhá záujem
 * či newsletter, osoba už existuje — preto len varovanie a volajúci ju dostane (opakovanie by narazilo
 * na duplicitu). Chybu založenia ukazuje sprievodca inline.
 */
export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ payload, interest, newsletter }: CreateContactInput) => {
      const { data: saved } = await api.post<ContactDetail>('/contacts', payload, { skipErrorToast: true })
      try {
        if (interest) await api.post(`/contacts/${saved.id}/interests`, interest, { skipErrorToast: true })
        if (newsletter) await api.post(newsletterPath(saved.id), { subscribed: true }, { skipErrorToast: true })
      } catch (err) {
        toast.warning(`Kontakt bol uložený, záujem alebo newsletter sa nepodarilo nastaviť: ${getApiErrorMessage(err, 'neznáma chyba')}`)
      }
      return saved
    },
    onSuccess: () => invalidateContactViews(qc),
  })
}

/** Prihlási / odhlási osobu z newslettera. */
export function useSetContactNewsletter(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (subscribed: boolean) => api.post(newsletterPath(id), { subscribed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: contactKeys.detail(id) }),
  })
}

/**
 * Vytvorí osobe účet s rolou Tiper. Nový tiper patrí aj do výberu tipera (TiperPicker) a zoznamu tiperov —
 * bez obnovy `tiperKeys.all` by ho výber do minúty (staleTime) neponúkol.
 */
export function useCreateContactTiperAccess(id: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/contacts/${id}/tiper-access`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.detail(id ?? '') })
      qc.invalidateQueries({ queryKey: tiperKeys.all })
    },
  })
}

/** Do koša. 403 nesie dôvod (iný agent, záujem, obchod, nehnuteľnosť) — ukáže ho toast interceptora. */
export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: (_res, id) => invalidateContactViews(qc, id),
  })
}
